import { Send } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import { useMessages, usePostMessage } from "@/hooks/use-groups";
import { t } from "@/i18n/pl";
import { formatMessageTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { ChatMessage } from "@/types/api";

// Mirrors `MessageCreate.body` max_length on the API.
const MAX_MESSAGE_LENGTH = 400;

function Bubble({ message, own }: { message: ChatMessage; own: boolean }) {
  // System messages (joins, day statuses) sit centered, quiet but visible.
  if (message.sender === null) {
    return (
      <div className="animate-in fade-in flex justify-center duration-300">
        <span className="bg-secondary text-muted-foreground rounded-full px-3 py-1 text-xs">
          {message.body}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "animate-in fade-in slide-in-from-bottom-1 flex flex-col duration-300",
        own ? "items-end" : "items-start",
      )}
    >
      <div
        className={cn(
          "max-w-[85%] rounded-2xl px-3.5 py-2 text-sm break-words sm:max-w-[75%]",
          own
            ? "bg-primary text-primary-foreground rounded-br-sm"
            : "bg-card rounded-bl-sm border",
        )}
      >
        {!own && (
          <p className="text-accent-foreground/70 mb-0.5 text-xs font-semibold">
            {message.sender.full_name}
          </p>
        )}
        <p>{message.body}</p>
      </div>
      <span className="text-muted-foreground mt-1 px-1 text-[11px] tabular-nums">
        {formatMessageTime(message.created_at)}
      </span>
    </div>
  );
}

export function ChatPanel({ groupId }: { groupId: string }) {
  const { user } = useAuth();
  const { data: messages } = useMessages(groupId, true);
  const postMessage = usePostMessage(groupId);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  // Always-visible counter; turns red for the last 50 characters.
  const nearLimit = draft.length >= MAX_MESSAGE_LENGTH - 50;

  // Pin to the newest message — on first load and whenever one arrives.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages?.length]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const body = draft.trim();
    if (!body || postMessage.isPending) return;
    postMessage.mutate(body, { onSuccess: () => setDraft("") });
  }

  return (
    <div className="bg-background flex h-full min-h-0 flex-col">
      <div
        ref={scrollRef}
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4"
      >
        {messages && messages.length === 0 && (
          <p className="text-muted-foreground m-auto text-center text-sm">{t.chat.empty}</p>
        )}
        {messages?.map((message) => (
          <Bubble key={message.id} message={message} own={message.sender?.id === user?.id} />
        ))}
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-background sticky bottom-0 flex items-center gap-2 border-t p-3"
      >
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t.chat.placeholder}
          aria-label={t.chat.placeholder}
          maxLength={MAX_MESSAGE_LENGTH}
          className="h-11 flex-1 rounded-full"
        />
        <Button
          type="submit"
          size="icon"
          aria-label={t.chat.send}
          disabled={!draft.trim() || postMessage.isPending}
          className="size-11 shrink-0 rounded-full"
        >
          <Send aria-hidden />
        </Button>
      </form>
      <p
        className={cn(
          "bg-background px-3 pb-2 text-center text-[11px]",
          nearLimit ? "text-destructive" : "text-muted-foreground/70",
        )}
      >
        {t.chat.remaining(draft.length, MAX_MESSAGE_LENGTH)}
        {" · "}
        {t.chat.retention}
      </p>
    </div>
  );
}
